import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, SwipeableDrawer } from '@mui/material';
import cardItems from "@/constants/cards.json";
import CardImage2 from "@/components/reading/CardImage2";
import CardImage from "@/components/reading/CardImage";
import useCards from '@/hooks/useCards';
import CardDialog from '@/components/reading/CardDialog';
import Matrix from "./spreads/Matrix";
import Pyramid from "./spreads/Pyramid";
import CelticCross from "./spreads/CelticCross";
import HorseshoeSpread from "./spreads/HorseshoeSpread";
import TetraktysSpread from "./spreads/TetraktysSpread";
import TreeOfLifeSpread from "./spreads/TreeOfLifeSpread";
import AstrologicalSpread from "./spreads/AstrologicalSpread";
import YearAheadSpread from "./spreads/YearAheadSpread";
import ThreeCardSpread from "./spreads/ThreeCardSpread";
import OneCardSpread from "./spreads/OneCardSpread";
import { track } from "@/third-party/MixPanel";
const spreadTypes = [
  "one-card-reading",
  "3x3",
  "three-card",
  "celtic-cross",
  "horseshoe-spread",
  "tetraktys-spread",
  "tree-of-life",
  "astrological-spread",
  "year-ahead-spread",
];
interface SpreadProps {
  type: any;
  cards?: TarotCard[] | null;
  cardDescriptions?: { [key: string]: any };
  setDrawnCards?: (cards: TarotCard[]) => void;
  // Remove shouldDrawNewCards prop as it's causing the loop
}

interface TarotCard {
  id: number;
  card_name?: string;
  description?: {
    primary_meaning: string;
    interpretation: string;
    card_name?: string; // Make this optional to match CardsContext
  };
  visible?: boolean;
  reversed?: boolean;
}

const Spread: React.FC<SpreadProps> = ({ cards = null, type, cardDescriptions = {}, setDrawnCards }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(140);
  const [displaySpread, setDisplaySpread] = useState<boolean>(true); // Start with true to prevent flash
  const [selectedCard, setSelectedCard] = useState<TarotCard | null>(null);
  const { drawnCards } = useCards(); // Remove drawCards from destructuring



  const getSelectedReadingType = (metadata: any) => {
    const type = metadata?.selectedReading?.type
    if (type && spreadTypes.includes(String(type).toLowerCase())) {
      return String(type).toLowerCase();
    }
    if (metadata?.spread_type && spreadTypes.includes(String(metadata?.spread_type).toLowerCase())) {
      return String(metadata?.spread_type).toLowerCase();
    }

    if (metadata?.type && spreadTypes.includes(String(metadata?.type).toLowerCase())) {
      return String(metadata?.type).toLowerCase();
    }

    return String(type?.type).toLowerCase();
  }

  const selectedReadingType = getSelectedReadingType(type);


  // console.log('selectedReadingType', selectedReadingType, type);
  // Handle both reading page and display reading page cases - use useMemo to prevent unnecessary re-renders
  const currentCards = useMemo(() => cards || drawnCards || [], [cards, drawnCards]);

  // Get setup from either type format
  const setup = type?.setup || type?.layout?.map((l: any) => parseInt(l.position)) || Array.from({ length: 9 }, (_, i) => i);


  // Calculate card width based on container size - optimized to prevent constant recalculation
  useEffect(() => {
    const updateCardSize = () => {
      if (!containerRef.current) return;
      const container = containerRef.current;

      // Calculate card width based on spread type
      let maxWidth;
      if (selectedReadingType === "one-card-reading") {
        maxWidth = Math.min(container.offsetWidth / 2.5, 140);
      } else if (selectedReadingType === "celtic-cross") {
        maxWidth = Math.min(container.offsetWidth / 4, 80);
      } else if (selectedReadingType === "horseshoe-spread" || selectedReadingType === "arc") {
        maxWidth = Math.min(container.offsetWidth / 7, 80);
      } else if (selectedReadingType === "tetraktys-spread") {
        maxWidth = Math.min(container.offsetWidth / 3, 80);
      } else if (selectedReadingType === "tree-of-life") {
        maxWidth = Math.min(container.offsetWidth / 5, 80);
      } else if (selectedReadingType === "astrological-spread") {
        maxWidth = Math.min(container.offsetWidth / 6, 70);
      } else if (selectedReadingType === "year-ahead-spread") {
        maxWidth = Math.min(container.offsetWidth / 6, 70);
      } else if (selectedReadingType === "three-card") {
        maxWidth = Math.min(container.offsetWidth / 3.5, 100);
      } else {
        // For grid layouts (3x3, pyramid)
        maxWidth = (container.offsetWidth / 3) - 21;
      }

      setCardWidth(maxWidth + 10);
    };

    // Initial calculation without timeout to prevent resize effect
    updateCardSize();

    window.addEventListener('resize', updateCardSize);
    return () => {
      window.removeEventListener('resize', updateCardSize);
    };
  }, [type?.type]); // Only depend on type change

  const handleCardClick = (card: any) => {
    if (card?.id && card?.description) {
      track('spread_card_meaning_selected', {
        card_id: card.id,
        card_name: card.description.card_name || '',
        reversed: card.reversed
      });
      setSelectedCard(card);
    }
  };

  const getCard = (index: number) => {
    let isActive: boolean;
    let cardPosition: number;

    if (selectedReadingType === "celtic-cross" || selectedReadingType === "horseshoe-spread" || selectedReadingType === "arc" || selectedReadingType === "tetraktys-spread" || selectedReadingType === "tree-of-life" || selectedReadingType === "astrological-spread" || selectedReadingType === "year-ahead-spread" || selectedReadingType === "three-card") {
      isActive = true;
      cardPosition = index;
    } else {
      isActive = setup.includes(index);
      cardPosition = setup.indexOf(index);
    }

    // Only get card if position is active and within bounds
    const card = isActive && cardPosition !== -1 ? currentCards[index] : null;
    const id = card ? getCardId(card) : -1;
    const isvisible = card?.visible || false;

    const functionalCard = (
      <div
        onClick={() => { if (card?.visible) { handleCardClick(card) } }}
        key={`card-container-${index}`}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'visible' // Ensure cards can render at full size
        }}
      >
        <CardImage2
          key={`card-image-${id}`}
          id={id}
          visible={isvisible}
          width={cardWidth}
          description={card?.description}
          reversed={card?.reversed}
          showFront={card?.showFront}
          onCardClick={() => handleCardClick(card)}
        />
      </div>
    );

    const transparentCard = (
      <Box
        key={`transparent-card-${index}`}
        sx={{
          backgroundColor: 'transparent',
          border: '0px solid #1c4a3d',
          borderRadius: 1,
          aspectRatio: '2/3',
        }}>
      </Box>
    );

    return card ? functionalCard : transparentCard;
  };

  // Update the getCardId function to properly handle the cardItems structure
  const getCardId = (card: any): number => {

    if (typeof card?.id === 'number') return card.id;
    if (card?.card_name) {
      return cardItems.cards.find(c => c.card_name === card.card_name)?.id ?? -1;
    }
    if (card?.card_name) {
      return cardItems.cards.find(c => c.card_name === card.card_name)?.id ?? -1;
    }
    return -1;
  };

  const calculateMinHeight = (selectedReadingType: string) => {

    if (selectedReadingType === "one-card-reading") return 300;
    if (selectedReadingType === "3x3") return 600;
    if (selectedReadingType === "pyramid") return 600;
    if (selectedReadingType === "three-card") return 200;
    if (selectedReadingType === "celtic-cross") return 600;
    if (selectedReadingType === "horseshoe-spread" || selectedReadingType === "arc") return 600;
    if (selectedReadingType === "tetraktys-spread") return 650;
    if (selectedReadingType === "tree-of-life") return 800;
    if (selectedReadingType === "astrological-spread") return 400;
    if (selectedReadingType === "year-ahead-spread") return 400;
    return 600;
  }

  return (
    <div id="spread-container" style={{ height: 'auto' }} className=" ">
      <div ref={containerRef} style={{ minHeight: `${calculateMinHeight(selectedReadingType)}px` }}>
        {selectedReadingType === "one-card-reading" && <OneCardSpread size={1} displaySpread={displaySpread} getCard={getCard} />}
        {selectedReadingType === "3x3" && <Matrix size={3} displaySpread={displaySpread} getCard={getCard} />}
        {selectedReadingType === "pyramid" && <Pyramid size={3} displaySpread={displaySpread} getCard={getCard} />}
        {(selectedReadingType === "three-card" || selectedReadingType === "3x1") && <ThreeCardSpread size={3} displaySpread={displaySpread} getCard={getCard} />}
        {(selectedReadingType === "celtic-cross") && <CelticCross size={3} cardWidth={cardWidth} displaySpread={displaySpread} getCard={getCard} />}
        {(selectedReadingType === "horseshoe-spread" || selectedReadingType === "arc") && <HorseshoeSpread size={3} spacing={cardWidth / 5} cardWidth={cardWidth} displaySpread={displaySpread} getCard={getCard} />}
        {selectedReadingType === "tetraktys-spread" && <TetraktysSpread size={3} spacing={cardWidth / 6} cardWidth={cardWidth} displaySpread={displaySpread} getCard={getCard} />}
        {selectedReadingType === "tree-of-life" && <TreeOfLifeSpread size={3} spacing={cardWidth / 7} cardWidth={cardWidth} displaySpread={displaySpread} getCard={getCard} />}
        {(selectedReadingType === "astrological-spread" || selectedReadingType === "circle") && <AstrologicalSpread size={3} spacing={cardWidth / 8} cardWidth={cardWidth} displaySpread={displaySpread} getCard={getCard} />}
        {(selectedReadingType === "year-ahead-spread") && <YearAheadSpread size={3} spacing={cardWidth / 8} cardWidth={cardWidth} displaySpread={displaySpread} getCard={getCard} />}

        <SwipeableDrawer
          anchor="bottom"
          open={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onOpen={() => { }}
          disableSwipeToOpen={true}
          slotProps={{
            paper: {
              sx: {
                height: '700px',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                padding: '20px'
              }
            }
          }}
        >
          {selectedCard && (
            <Box sx={{ p: 0, pt: 0 }}>
              <CardDialog
                open={!!selectedCard}
                onClose={() => setSelectedCard(null)}
                card={{
                  reversed: selectedCard.reversed || false,
                  id: selectedCard.id,
                  card_name: selectedCard.description?.card_name || '',
                  primary_meaning: selectedCard.description?.primary_meaning || '',
                  interpretation: selectedCard.description?.interpretation || ''
                }}
              />
            </Box>
          )}
        </SwipeableDrawer>
      </div>
    </div>
  );
};

export default Spread;
